/* istanbul ignore file */
import {
    ApolloClient,
    ApolloProvider,
    createHttpLink,
    InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import "@fortawesome/fontawesome-free/js/brands";
import "@fortawesome/fontawesome-free/js/fontawesome";
import "@fortawesome/fontawesome-free/js/regular";
import "@fortawesome/fontawesome-free/js/solid";
import { loadableReady } from "@loadable/component";
import * as Sentry from "@sentry/react";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import type { AppType } from "./app";
import { App } from "./app";
import { ScrollToTop } from "./components/scroll-to-top";
import { FlagProvider, FlagService } from "./service/flags-provider";
import { UserState, USER_KEY } from "./service/reducers/user";
import "./styles/index.scss";

if (process.env.SENTRY_DSN && process.env.SENTRY_DSN !== "undefined") {
    Sentry.init({
        dsn: process.env.SENTRY_DSN,
        environment: window.location.hostname,
    });
}

// Check that service workers are supported
if ("serviceWorker" in navigator) {
    // Use the window load event to keep the page load performant
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    window.addEventListener("load", async () => {
        try {
            const registration = await navigator.serviceWorker.register(
                "/service-worker.js"
            );
            console.info("SW registered: ", registration);
        } catch (e: unknown) {
            console.warn("Could't start the service worker", e);
        }
    });
}

// eslint-disable-next-line no-underscore-dangle
const apolloCache = window.__APOLLO_STATE__;

const authLink = setContext((_, { headers }) => {
    const user = JSON.parse(localStorage.getItem(USER_KEY) ?? "") as UserState;

    return {
        headers: {
            ...(headers as unknown[]),
            authorization: user?.strapiToken
                ? `Bearer ${user?.strapiToken}`
                : "",
        },
    };
});

const httpLink = createHttpLink({
    uri: process.env.GRAPHQL_URL,
});

const apolloClient = new ApolloClient({
    cache: new InMemoryCache().restore(apolloCache),
    link: authLink.concat(httpLink),
    ssrMode: true,
});
const flagClient = new FlagService();

const renderApp = async (AppTree: AppType) => {
    const root = document.getElementById("root");
    type RenderMethod = keyof Pick<typeof ReactDOM, "hydrate" | "render">;
    let method: RenderMethod = "hydrate";

    if (root?.childNodes.length === 0) {
        method = "render";
    }

    await Promise.all([loadableReady(), flagClient.start()]);

    ReactDOM[method](
        <React.StrictMode>
            <ApolloProvider client={apolloClient}>
                <FlagProvider client={flagClient}>
                    <BrowserRouter>
                        <ScrollToTop />
                        <AppTree />
                    </BrowserRouter>
                </FlagProvider>
            </ApolloProvider>
        </React.StrictMode>,
        root
    );
};

renderApp(App);

if (module.hot) {
    const { hot } = module;

    hot.accept("./app.tsx", () => {
        // eslint-disable-next-line @typescript-eslint/no-var-requires,@typescript-eslint/no-unsafe-assignment,global-require
        const { App: nextApp } = require("./app");

        renderApp(nextApp);
    });
}
